/**
 * v2.44 — Floating "API Health" pill (admin-only).
 *
 * Fixed bottom-left, polls `/api/admin/health/aggregate` every 60 seconds.
 * Renders ONLY when the signed-in user's email matches OWNER_EMAILS — this
 * is defense-in-depth on top of the backend's 403 check.
 *
 * UX
 *  - Idle: tiny pill, colored dot + "OK 280ms" (or "Slow 1.2s" / "Issue")
 *  - Click: expands to show per-endpoint breakdown (latency + status icon)
 *  - Outside-click / Esc: collapses
 *
 * Refresh button forces an immediate re-check (useful right after deploy).
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";
import { Activity, ChevronUp, RefreshCw, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";


const LEVEL_STYLES = {
  green:  { dot: "bg-emerald-400", ring: "ring-emerald-400/30", label: "All systems OK" },
  yellow: { dot: "bg-amber-400",   ring: "ring-amber-400/30",   label: "Degraded" },
  red:    { dot: "bg-rose-500",    ring: "ring-rose-500/40",    label: "Issues detected" },
  unknown:{ dot: "bg-neutral-500", ring: "ring-neutral-500/30", label: "Checking…" },
};

function fmtMs(ms) {
  if (ms == null) return "—";
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

export default function ApiHealthPill() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [data, setData] = useState(null);
  const wrapRef = useRef(null);

  // Role-based — visible to owners and admins (not hardcoded to a single email).
  const role = (user?.role || "").toLowerCase();
  const isOwner = role === "owner" || role === "admin";

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/health/aggregate");
      setData(r.data);
    } catch (e) {
      setData({ overall: "red", endpoints: [], error: e?.response?.status === 403 ? "Not admin" : "Network" });
    } finally {
      setLoading(false);
    }
  };

  const clearCache = async () => {
    // Quick guard so an accidental click doesn't nuke prod caches.
    if (!window.confirm("Clear server caches?\n\nThis wipes /collections-bundle + /home-bundle Mongo + memory caches. Rails will refetch from TMDB on the next request (cold but fresh).")) {
      return;
    }
    setClearing(true);
    try {
      const r = await api.post("/admin/cache/clear?scope=rails");
      const msg = `Cache cleared — memory:${r.data.memory_cleared} coll:${r.data.coll_cache_cleared} mongo:${r.data.mongo_cleared}`;
      toast.success(msg);
      await refresh();
    } catch (e) {
      alert(`Cache clear failed: ${e?.response?.data?.detail || e?.message || "unknown"}`);
    } finally {
      setClearing(false);
    }
  };

  useEffect(() => {
    if (!isOwner) return;
    refresh();
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  if (!isOwner) return null;

  const level = data?.overall || "unknown";
  const style = LEVEL_STYLES[level] || LEVEL_STYLES.unknown;
  const slowest = data?.endpoints?.reduce((acc, e) => (e.ms > (acc?.ms || -1) ? e : acc), null);

  return (
    <div
      ref={wrapRef}
      className="fixed bottom-5 left-5 z-30"
      data-testid="api-health-wrap"
    >
      {/* Expanded panel — rendered above the pill so it grows upward */}
      {open && data && (
        <div
          className="mb-2 w-[280px] rounded-xl border border-white/15 bg-[#0E0E0E]/95 backdrop-blur-md shadow-2xl p-3 fm-fade"
          data-testid="api-health-panel"
        >
          <div className="flex items-center justify-between mb-2 pb-2 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Activity className="w-3.5 h-3.5 text-white/70" />
              <span className="text-xs font-bold uppercase tracking-wider text-white/85">
                API Health
              </span>
            </div>
            <button
              onClick={refresh}
              data-testid="api-health-refresh"
              disabled={loading}
              className="text-white/60 hover:text-white p-1 rounded transition disabled:opacity-40"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>

          <div className="space-y-1.5">
            {(data.endpoints || []).map((ep) => {
              const lvl = ep.level || "unknown";
              const Icon = lvl === "green" ? CheckCircle2 : lvl === "yellow" ? AlertTriangle : XCircle;
              const iconCls =
                lvl === "green" ? "text-emerald-400" : lvl === "yellow" ? "text-amber-400" : "text-rose-400";
              return (
                <div
                  key={ep.name}
                  className="flex items-center justify-between text-[11px]"
                  data-testid={`api-health-ep-${ep.name}`}
                >
                  <div className="flex items-center gap-1.5 text-white/80">
                    <Icon className={`w-3 h-3 ${iconCls}`} />
                    <span className="font-mono">{ep.name}</span>
                  </div>
                  <span className="font-mono text-white/60">
                    {ep.ok ? fmtMs(ep.ms) : (ep.error?.slice(0, 18) || "fail")}
                    {ep.empty ? " · empty" : ""}
                  </span>
                </div>
              );
            })}
          </div>

          {data.checked_at && (
            <div className="mt-2 pt-2 border-t border-white/10 flex items-center justify-between text-[10px] text-white/45 font-mono">
              <span>{new Date(data.checked_at).toLocaleTimeString()}</span>
              <button
                onClick={clearCache}
                disabled={clearing}
                data-testid="api-health-clear-cache"
                className="px-2 py-0.5 rounded border border-rose-400/30 text-rose-300/90 hover:bg-rose-400/10 hover:text-rose-200 transition disabled:opacity-40 text-[10px] uppercase tracking-wider"
                title="Wipe server-side rail caches (memory + Mongo). Use when rails go empty after a deploy."
              >
                {clearing ? "Clearing…" : "Clear cache"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Pill */}
      <button
        onClick={() => setOpen((s) => !s)}
        data-testid="api-health-pill"
        title={`API Health: ${style.label}${slowest ? ` (slowest: ${slowest.name} ${fmtMs(slowest.ms)})` : ""}`}
        className={`group flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/85 border border-white/15 backdrop-blur-md shadow-lg hover:border-white/30 transition-all ring-2 ${style.ring}`}
      >
        <span className={`w-2 h-2 rounded-full ${style.dot} ${level === "green" && !loading ? "animate-pulse" : ""}`} />
        <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-white/80">
          {level === "unknown" ? "Health" : (slowest ? fmtMs(slowest.ms) : "OK")}
        </span>
        <ChevronUp className={`w-3 h-3 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
    </div>
  );
}
